/**
 * ProfileMenu — User profile and settings dropdown menu
 *
 * Displays user profile information and quick links to settings
 * Shows Settings option only for admin users
 */

import { useState, useRef, useEffect } from 'react';
import { LogOut, Settings, User } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../lib/utils';

export interface ProfileMenuProps {
  className?: string;
}

export function ProfileMenu({ className }: ProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, clearAuth } = useAuthStore();

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      clearAuth();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div ref={menuRef} className={cn('relative', className)}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'p-2 rounded-lg transition-all duration-200',
          'text-gray-600 dark:text-gray-400',
          'hover:text-gray-900 dark:hover:text-gray-200',
          'hover:bg-gray-100 dark:hover:bg-metallic-800',
          isOpen && 'bg-gray-100 dark:bg-metallic-800 text-gray-900 dark:text-white'
        )}
        title="Profile"
        aria-label="Open profile menu"
        aria-expanded={isOpen}
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-metallic-900 rounded-lg shadow-lg border border-gray-200 dark:border-metallic-800 z-50 py-1">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-metallic-800">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {user?.name || 'User'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {user?.email || 'email@example.com'}
            </p>
            {user?.organization && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                Org: {user.organization}
              </p>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* Profile */}
            <a
              href="/profile"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-metallic-800 transition-colors"
            >
              <User className="w-4 h-4" />
              <span>Profile Settings</span>
            </a>

            {/* Settings - Only for admin users */}
            {user?.role === 'admin' && (
              <a
                href="/admin"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-metallic-800 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Admin Settings</span>
              </a>
            )}
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 dark:border-metallic-800 py-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileMenu;

// Made with Bob
